import { useState, useCallback, useEffect, useRef } from 'react';
import { useTriggerValidation } from './useTriggerValidation';

export const useTriggerForm = (initialData = null) => {
  const initializedRef = useRef(false);
  // Capturar el nombre original del trigger que se carga inicialmente
  const originalTriggerNameRef = useRef(null);
  
  // Determinar si es un trigger nuevo o existente
  const isNewTrigger = initialData === null;
  
  const {
    errors,
    formState,
    validateName,
    validateDescription,
    validateAll,
    clearErrors,
    hasValidationErrors,
  } = useTriggerValidation(originalTriggerNameRef, isNewTrigger);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    category: "",
    ttlValue: "",
    ttlUnit: "hours",
    isNoTimeLimit: false,
    active: false,
  });
  
  useEffect(() => {
    if (initialData && !initializedRef.current) {
      const hasCompleteData = initialData.uuid || initialData.id || initialData.workflowCatalogUuid;
      const hasName = initialData.name;
      
      if (!hasCompleteData || !hasName) {
        return;
      }
      
      const mappedData = {
        name: initialData.name || "",
        description: initialData.description || "",
        type: initialData.type || "",
        category: initialData.category || "",
        ttlValue: initialData.ttlValue || "",
        ttlUnit: initialData.ttlUnit || "hours",
        isNoTimeLimit: initialData.isNoTimeLimit || false,
        active: initialData.active || false,
      };
      
      setFormData(mappedData);
      originalTriggerNameRef.current = initialData.name || null;
      initializedRef.current = true;
    } else if (initialData && initializedRef.current) {
      const hasCompleteData = initialData.uuid || initialData.id || initialData.workflowCatalogUuid;
      const hasName = initialData.name;
      
      if (hasCompleteData && hasName) {
        initializedRef.current = false;
      }
    }
  }, [initialData, validateName, validateDescription]);

  useEffect(() => {
    if (initializedRef.current && formData.name && formData.name.trim() !== '' && formData.description !== undefined) {
      setTimeout(() => {
        validateName(formData.name);
        validateDescription(formData.description);
      }, 100);
    }
  }, [formData.name, formData.description, validateName, validateDescription]);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNameChange = useCallback((value) => {
    updateFormData('name', value);
    validateName(value);
  }, [updateFormData, validateName]);

  const handleDescriptionChange = useCallback((value) => {
    updateFormData('description', value);
    validateDescription(value);
  }, [updateFormData, validateDescription]);

  const handleTypeChange = useCallback((value) => {
    updateFormData('type', value);
  }, [updateFormData]);

  const handleCategoryChange = useCallback((value) => {
    updateFormData('category', value);
  }, [updateFormData]);

  const handleTTLValueChange = useCallback((value) => {
    updateFormData('ttlValue', value);
  }, [updateFormData]);

  const handleTTLUnitChange = useCallback((value) => {
    updateFormData('ttlUnit', value);
  }, [updateFormData]);

  const handleNoTimeLimitChange = useCallback((value) => {
    updateFormData('isNoTimeLimit', value);
  }, [updateFormData]);

  const handleActiveChange = useCallback((value) => {
    updateFormData('active', value);
  }, [updateFormData]);

  const getFormData = useCallback(() => {
    return formData;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      description: "",
      type: "",
      category: "",
      ttlValue: "",
      ttlUnit: "hours",
      isNoTimeLimit: false,
      active: false,
    });
    clearErrors();
    initializedRef.current = false;
  }, [clearErrors]);

  const isDirty = useCallback(() => {
    if (!initialData) return false;
    
    return (
      formData.name !== (initialData.name || "") ||
      formData.description !== (initialData.description || "") ||
      formData.type !== (initialData.type || "") ||
      formData.category !== (initialData.category || "") ||
      formData.ttlValue !== (initialData.ttlValue || "") ||
      formData.ttlUnit !== (initialData.ttlUnit || "hours") ||
      formData.isNoTimeLimit !== (initialData.isNoTimeLimit || false) ||
      formData.active !== (initialData.active || false)
    );
  }, [formData, initialData]);

  const isFormValid = useCallback(() => {
    return validateAll(formData);
  }, [validateAll, formData]);

  return {
    formData,
    errors,
    formState,
    handleNameChange,
    handleDescriptionChange,
    handleTypeChange,
    handleCategoryChange,
    handleTTLValueChange,
    handleTTLUnitChange,
    handleNoTimeLimitChange,
    handleActiveChange,
    getFormData,
    resetForm,
    isDirty,
    updateFormData,
    isFormValid,
    validateAll,
    hasValidationErrors,
  };
}; 